import {
    registrationRequestToken,
    registrationExchangeToken,
    registrationRefreshToken,
    registrationReleaseToken,
    registrations,
    releaseToken,
    withToken,
} from '@pexip/infinity-api';

import {logger} from './logger';
import {createEventSourceManager} from './eventSource';
import {createRequestClient} from './requestClient';
import type {Incoming, RegistrationSignals, RequestClient} from './types';
import {createRegistrationSignals} from './utils';

export const createRegistration = ({
    node = window.location.host,
    host = `${window.location.protocol}//${node}`,
    registrationSignals = createRegistrationSignals(),
}: {
    node?: string;
    host?: string;
    registrationSignals?: RegistrationSignals;
}) => {
    const props = {
        alias: undefined as string | undefined,
        chosenIdp: undefined as string | undefined,
        requestClient: undefined as RequestClient | undefined,
        eventSourceManager: undefined as
            ReturnType<typeof createEventSourceManager> | undefined,
    };

    const register = async ({
        ssoToken,
        chosenIdp,
        alias = props.alias,
        username,
        password,
        exchangeToken,
        authToken,
    }: {
        ssoToken?: string;
        chosenIdp?: string;
        alias?: string;
        username?: string;
        password?: string;
        exchangeToken?: boolean;
        authToken?: string;
    }) => {
        if (!alias) {
            throw new Error('No alias provided for registration');
        }

        // Clean up any previous registration before starting a new one
        await unregister();

        props.alias = alias;
        props.chosenIdp = chosenIdp;

        logger.debug(`Registering server ${host} with alias ${alias}`);
        const req = exchangeToken
            ? await registrationExchangeToken({
                  fetcher: authToken ? withAuthToken(fetch, authToken) : fetch,
                  host,
                  params: {alias},
              })
            : await registrationRequestToken({
                  fetcher:
                      username && password
                          ? withAuthorizationToken(fetch, username, password)
                          : fetch,
                  host,
                  params: {
                      alias,
                      node,
                      ssoToken: ssoToken ?? '',
                      chosenIdp: chosenIdp ?? '',
                  },

                  body: {
                      ...(ssoToken ? {sso_token: ssoToken} : {}),
                      ...(chosenIdp ? {chosen_idp: chosenIdp} : {}),
                  },
              });

        switch (req.status) {
            case 200:
                logger.debug('Registration successful');
                props.requestClient = createRequestClient({
                    token: req.data.result.token,
                    expires: Number(req.data.result.expires),
                    refreshTokenFn: fetcher =>
                        registrationRefreshToken({
                            fetcher,
                            host,
                            params: {alias},
                        }).then(res => (res.status === 200 ? res : undefined)),
                    releaseTokenFn: (fetcher, body) =>
                        registrationReleaseToken({
                            fetcher,
                            host,
                            params: {alias},
                            body,
                        }).then(() => undefined),
                    releaseTokenBeaconUrl: (token: string) => {
                        const url = new URL(
                            `/api/client/v2/registrations/${encodeURIComponent(alias)}/release_token`,
                            host,
                        );
                        url.searchParams.set('token', token);
                        return url.href;
                    },
                });
                props.eventSourceManager = createEventSourceManager({
                    getToken: () => props.requestClient?.token,
                    createEventSource: token => {
                        const eventsUrl = new URL(
                            `/api/client/v2/registrations/${encodeURIComponent(alias)}/events`,
                            host,
                        );
                        eventsUrl.searchParams.set('token', token);
                        const es = new EventSource(eventsUrl.href);

                        const addRegistrationEventListener = <
                            K extends keyof RegistrationSignals,
                        >(
                            eventName: string,
                            signalKey: K,
                        ) => {
                            es.addEventListener(eventName, event => {
                                try {
                                    const data = JSON.parse(
                                        (event as globalThis.MessageEvent).data,
                                    );
                                    registrationSignals[signalKey].emit(data);
                                    logger.debug(
                                        {data},
                                        `${eventName} event received`,
                                    );
                                } catch (error) {
                                    logger.error(
                                        {error},
                                        `Failed to parse ${eventName} event data`,
                                    );
                                }
                            });
                        };

                        addRegistrationEventListener('incoming', 'onIncoming');
                        addRegistrationEventListener(
                            'incoming_cancelled',
                            'onIncomingCancelled',
                        );

                        return es;
                    },
                    onMaxReconnectAttemptsReached: () => {
                        logger.error(
                            'Registration EventSource max reconnect attempts reached',
                        );
                    },
                });
                await props.eventSourceManager.connect();
                break;
            default:
                logger.debug(
                    `Registration failed with status ${req.status}: ${req.data}`,
                );
        }

        return req;
    };

    const directorySearch = async ({query}: {query: string}) => {
        logger.debug(
            `Performing directory search on server ${host} with query ${query}`,
        );
        if (!props.requestClient) {
            throw new Error('No token available for registration');
        }
        const req = await registrations({
            fetcher: props.requestClient.fetcher,
            host,
            params: {query},
        });

        if (req.status !== 200) {
            logger.debug(
                `Directory search failed with status ${req.status}: ${req.data}`,
            );
        }

        return req;
    };

    const unregister = async () => {
        props.eventSourceManager?.close();
        props.eventSourceManager = undefined;
        await props.requestClient?.cleanup();
        props.requestClient = undefined;
        props.alias = undefined;
    };

    const declineIncomingCall = async (
        incoming: Pick<Incoming, 'token' | 'conference_alias'>,
    ) => {
        logger.debug(
            `Releasing incoming call token for ${incoming.conference_alias}`,
        );
        return releaseToken({
            fetcher: withToken(fetch, incoming.token),
            host,
            params: {conferenceAlias: incoming.conference_alias},
            body: {},
        });
    };

    return {
        register,
        unregister,
        directorySearch,
        declineIncomingCall,
        get registrationToken() {
            return props.requestClient?.token;
        },
    };
};

export function withAuthorizationToken(
    fetcher: typeof fetch,
    username: string,
    password: string,
): typeof fetch {
    return (
        requestInfo: RequestInfo | URL,
        requestInit: RequestInit | undefined,
    ) => {
        if (!username || !password) {
            throw new Error('No username or password provided');
        }
        const headers = new Headers(requestInit?.headers);
        headers.set(
            'X-Pexip-Authorization',
            `x-pexip-basic ${btoa(`${username}:${password}`)}`,
        );
        return fetcher(requestInfo, {...requestInit, headers});
    };
}

function withAuthToken(fetcher: typeof fetch, token: string): typeof fetch {
    return (
        requestInfo: RequestInfo | URL,
        requestInit: RequestInit | undefined,
    ) => {
        const headers = new Headers(requestInit?.headers);
        headers.set('token', token);
        return fetcher(requestInfo, {...requestInit, headers});
    };
}
